'use client';

import React from 'react';
import { CorporateMembersBlock as ICorporateMembersBlock } from '../types';
import Image from 'next/image';
import LinkedInIcon from '@/assets/linkedin.svg';
import { SupportedLanguages } from '@/types/globals';

export default function CorporateMembersBlock({
  block,
  locale,
}: {
  block: ICorporateMembersBlock;
  locale: SupportedLanguages;
}) {
  // Check if locale is English or French
  const isEnglish = locale === 'en';
  // This is a little weird in WordPresss because we need to refrence the team_member post type instead of having a dedicated
  // post type for corporate members.
  const members = Array.isArray(block.team_members) ? block.team_members : [];

  return (
    <div className="mx-auto max-w-7xl bg-white px-4">
      {block.title && (
        <div className="mb-8 flex items-center">
          <span className="text-blue-grey font-inter text-base font-semibold tracking-widest whitespace-nowrap uppercase">
            {block.title}
          </span>
          <div className="bg-graphite-100 ml-4 h-px flex-1" />
        </div>
      )}
      <div className="flex flex-col gap-12">
        {members.map((member, idx) => (
          <div
            key={member.id || idx}
            className="grid grid-cols-1 items-start gap-2 lg:grid-cols-[30%_70%] lg:gap-8"
          >
            {/* Left Column - Image */}
            <div className="relative mx-auto aspect-[3/4] w-full max-w-xs overflow-hidden rounded-lg sm:max-w-sm lg:max-w-none">
              {member.profile_picture?.url && (
                <Image
                  src={member.profile_picture.url}
                  alt={member.profile_picture.alt || member.title}
                  fill
                  className="rounded-lg object-cover object-top"
                  sizes="(min-width: 1024px) 30vw, (min-width: 640px) 60vw, 80vw"
                />
              )}
            </div>
            {/* Right Column - Content */}
            <div className="space-y-2 px-2 sm:px-4 lg:px-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-source-serif-pro text-graphite-900 mb-2 text-3xl leading-[120%] font-semibold tracking-[-0.014em]">
                    {member.title}
                  </h3>
                  {isEnglish ? (
                    <p className="font-source-sans-pro text-qf-purple text-[14px] leading-[24px] font-semibold tracking-[0.10em] uppercase">
                      {member.position}
                    </p>
                  ) : (
                    <p className="font-source-sans-pro text-qf-purple text-[14px] leading-[24px] font-semibold tracking-[0.10em] uppercase">
                      {member.fr_position || ''}
                    </p>
                  )}
                </div>
                {member.linkedin_url && (
                  <button
                    onClick={() => window.open(member.linkedin_url, '_blank')}
                    className="flex-shrink-0"
                  >
                    <LinkedInIcon />
                  </button>
                )}
              </div>
              {isEnglish ? (
                <div
                  className="font-source-sans-pro text-graphite-900 max-w-none text-lg leading-[150%] font-normal"
                  dangerouslySetInnerHTML={{ __html: member.bio || '' }}
                />
              ) : (
                <div
                  className="font-source-sans-pro text-graphite-900 max-w-none text-lg leading-[150%] font-normal"
                  dangerouslySetInnerHTML={{ __html: member.fr_bio || '' }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
